import { useCallback, useEffect, useRef, useState } from 'react';

import { doc, getDoc, setDoc } from '@firebase/firestore';
import { FirebaseError } from 'firebase-admin';
import { FormikHelpers } from 'formik';
// `useUpdateStory` is exclusive to `hooks/pages/companySlug/create.hooks.ts`, which
// is exclusive to `app/[companySlug]/create/page.tsx` (research.md R3) — safe to
// convert to `next/navigation` in place rather than adding an `*AppRouter` sibling.
// `replace(href)` has the same signature on both routers, so this is import-only.
import { useRouter } from 'next/navigation';

import { IStory } from 'types/story';

import {
  getStory,
  updateStory,
  updateStoryCollection,
} from 'services/api/firestore/client';

import { db } from 'clients/firebase';

import { useSetupStoryState } from '../state';

export const useUpdateStory = (companySlug: string, draftId: string | null) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<FirebaseError | null>(null);
  const [initialData, setInitialData] = useState<IStory | null>(null);
  const { replace } = useRouter();
  const { lazySetupStoryState } = useSetupStoryState();
  // What the database holds, so a failed save can make the form dirty again.
  const lastSaved = useRef<IStory | null>(null);

  const handleSubmit = async (
    values: IStory,
    helpers: FormikHelpers<IStory>,
  ) => {
    helpers.resetForm({ values });
    helpers.setStatus({ saving: true });

    try {
      await updateStory({ story: values });
      lastSaved.current = values;
      helpers.setStatus({});
    } catch (e) {
      console.error(e);
      // The reset above made the form clean, so autosave would never retry. Reset
      // the baseline to the last saved story and put the current values back,
      // keeping edits made while the save was in flight, to make it dirty again.
      // Before `setStatus`, since `resetForm` also resets the status.
      if (lastSaved.current) {
        helpers.resetForm({ values: lastSaved.current });
        helpers.setValues(current => current, false);
      }
      helpers.setStatus({
        error: 'Could not save your changes. Please try again.',
      });
    }
  };

  useEffect(() => {
    if (!draftId) {
      replace(`/${companySlug}`);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    (async () => {
      // A draft just created via `useCreateStoryAppRouter` can race the read
      // right after the soft navigation lands, so retry once before erroring.
      const fetchStory = async (): Promise<IStory> => {
        try {
          return await getStory(draftId);
        } catch {
          return await getStory(draftId);
        }
      };

      try {
        const story = await fetchStory();

        lazySetupStoryState(draftId, 'editor', story.meta);
        lastSaved.current = story;
        setInitialData(story);
      } catch (e) {
        setError(e as FirebaseError);
      } finally {
        setIsLoading(false);
      }
    })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftId]);

  return {
    error,
    isLoading,
    initialData,
    handleSubmit,
  };
};

export const useMoveStoryInCollection = () => {
  const moveStoryInCollection = useCallback(
    async (storyId: string, collectionId: string) => {
      await updateStoryCollection({ storyId, collectionId });
    },
    [],
  );

  return { moveStoryInCollection };
};

export const useRemoveStoryFromCollection = () => {
  const removeStoryFromCollection = useCallback(async (storyId: string) => {
    await updateStoryCollection({ storyId });
  }, []);

  return { removeStoryFromCollection };
};

export const useAssignTeamsToStory = () => {
  const [isLoading, setIsLoading] = useState(false);

  const assignTeamsToStory = useCallback(
    async ({ storyId, team }: { storyId: string; team: string[] }) => {
      setIsLoading(true);

      const storyDoc = doc(db, `Stories/${storyId}`);
      let data: IStory;

      try {
        await setDoc(
          storyDoc,
          {
            meta: { team },
          },
          { merge: true },
        );

        const storySnapshot = await getDoc(storyDoc);
        data = storySnapshot.data() as IStory;
      } finally {
        setIsLoading(false);
      }

      return data;
    },
    [],
  );

  return { assignTeamsToStory, isLoading };
};
